import { BaseApi, ApiResponse } from "../utils/request";

export interface OpenRedPacket {
  id: string;
}
export interface RedPacket {
  id: string;
}

// 红包信息接口响应
export interface RedpacketInfo {
  id: string;
  status: number; // -3: 不满足条件（如VIP要求），其他: 正常
  type: string; // "FIXED": 固定金额，其他: 随机金额
  maxAmount: number; // 最大金额
  maxNumber: number; // 最大数量
  sendAmount?: number; // 已发送金额（固定红包使用，表示单个红包金额）
  sendNumber?: number; // 已发送数量
  customContent?: string; // 自定义文案内容（支持HTML格式，用于详情页展示）
  customMin?: number; // 单个红包最小金额（随机红包使用）
  customMax?: number; // 单个红包最大金额（随机红包使用）
  displayMinAmount?: number; // 显示最小金额（固定红包使用）
  displayMaxAmount?: number; // 显示最大金额（固定红包使用）
  [key: string]: any;
}

// 领取红包响应
export interface RedpacketGrapResponse {
  queue?: string; // 队列ID（如果有）
  id?: string; // 任务ID（如果有）
  [key: string]: any;
}

// 轮询查询状态响应
export interface QueueResultResponse {
  status: "WAIT" | "SUCCESS" | "FAILED" | string;
  res: {
    getAmount?: number; // 领取到的金额
    tipVOS?: Array<{
      tips: string; // 条件提示文本
      ok: boolean; // 是否满足条件
    }>; // 条件列表
    msg?: string; // 错误消息
    [key: string]: any;
  };
}

// 红包记录类型
export interface RedpacketRecordType {
  id: string;
  userId: string;
  redpacketId: string;
  amount: number;
  [key: string]: any;
}

// 红包限制日志类型
export interface RedpacketLimitLogType {
  id: string;
  userId: string;
  redPacketId: string;
  type:
    | "VIP_LIMIT"
    | "COUNTRY_LIMIT"
    | "BADUSER_LIMIT"
    | "RECHARGE_LIMIT"
    | "BET_LIMIT"
    | "CHANNEL_LIMIT"
    | "BRAND_LIMIT"
    | "DEVICE_LIMIT";
  createTime: string | number;
  [key: string]: any;
}

export interface ClaimDailyBetRewardResponse {
  id: string;
}

// 领取奖励请求/响应类型
export interface ClaimRewardRequest {
  activityId: string;
  claimRecordIds: string[];
}

export interface ClaimRewardResponse {
  claimableTiers?: any[];
  hasClaimable?: boolean;
  canClaim?: boolean;
  [key: string]: any;
}

// 查询充值促销活动请求/响应类型
export interface QueryRechargePromotionRequest {
  activityId: string;
  rechargeAmounts: number[];
}

export interface QueryRechargePromotionResponse {
  rechargePromotions?: Array<{
    rechargeAmount: number;
    discountAmount?: number;
    [key: string]: any;
  }>;
  [key: string]: any;
}

export class EventApi extends BaseApi {
  constructor() {
    super();
  }

  async openRedPacket(id: string): Promise<ApiResponse<OpenRedPacket, true>> {
    return this.post<OpenRedPacket, true>("/api/v1/get_red_packet", { id });
  }

  async redPacket(id: string): Promise<ApiResponse<RedPacket, true>> {
    return this.post<RedPacket, true>("/api/v1/red_packet", { id });
  }

  // 获取红包信息
  async redpacketInfo(id: string): Promise<ApiResponse<RedpacketInfo, true>> {
    return this.post<RedpacketInfo, true>("/api/redpacket/info", { id });
  }

  async claimDailyBetReward(
    ids: string[]
  ): Promise<ApiResponse<ClaimDailyBetRewardResponse, true>> {
    return this.post<ClaimDailyBetRewardResponse, true>(
      "/api/v1/claim_daily_bet_reward",
      { id: ids }
    );
  }

  // 领取奖励
  async claimReward(params: ClaimRewardRequest): Promise<ClaimRewardResponse> {
    const response = await this.post<ClaimRewardResponse, true>(
      "/api/activity/claimReward",
      params
    );
    return (response as any).data?.model || response.data;
  }

  // 领取充值+投注奖励
  async claimRechargeBet(claimId: string): Promise<any> {
    const response = await this.post<any, true>(
      "/api/activity/rechargeBet/claim",
      { claimId }
    );
    return (response as any).data?.model || response.data;
  }

  // 查询充值促销活动
  async queryRechargePromotion(
    params: QueryRechargePromotionRequest
  ): Promise<QueryRechargePromotionResponse> {
    const response = await this.post<QueryRechargePromotionResponse, true>(
      "/api/activity/queryRechargePromotion",
      params,
      {
        alert: false,
      }
    );
    return (response as any).data.model || response.data;
  }

  // 领取九宫格任务奖励（获得抽奖次数）
  // 成功后需要调用详情接口获取最新的抽奖次数和任务信息
  async claimNineGridLottery(activityId: string): Promise<void> {
    await this.get<any, true>(
      `/api/nineGrid/claimLottery?activityId=${activityId}`
    );
  }

  // 执行九宫格抽奖
  // 返回中奖信息，成功后需要调用详情接口获取最新的剩余次数
  async nineGridLottery(activityId: string): Promise<{
    prizeId: string | number;
    position: number;
    amount: number;
    bonusAuditRatio?: number;
    message?: string;
  }> {
    const response = await this.get<any, true>(
      `/api/nineGrid/lottery?activityId=${activityId}`
    );

    // 获取后端返回的原始数据
    const rawData = (response as any).data?.model || response.data;

    // 字段映射：后端字段 → 前端字段
    return {
      prizeId: rawData.lotteryRecordId,
      position: rawData.rewardItemNumber,
      amount: rawData.rewardAmount,
      bonusAuditRatio: rawData.bonusAuditRatio,
      message: rawData.message,
    };
  }

  // 获取九宫格活动详情
  // 返回活动的完整信息，包括用户进度、任务信息等
  async getNineGridDetail(activityId: string): Promise<any> {
    const response = await this.get<any, true>(
      `/api/nineGrid/detail?activityId=${activityId}`
    );

    // 返回完整的活动详情数据
    return (response as any).data?.model || response.data;
  }

  /**
   * 分组充值任务到账弹窗已读。有 ledgerId 时优先传流水主键（多轮同 claimId 时唯一）；
   * 否则传 claimId（兼容旧包）。
   */
  async markGroupedRechargeTaskPopupRead(
    payload: { ledgerId: string } | { claimId: string }
  ): Promise<void> {
    await this.post<any, true>(
      "/api/grouped_recharge_task/popup/read",
      payload
    );
  }
}

export const eventApi = new EventApi();

export const OpenRedPacket = (params: { id: string }) =>
  eventApi.openRedPacket(params.id);

export const RedPacket = (params: { id: string }) =>
  eventApi.redPacket(params.id);

export const RedpacketInfo = (params: { id: string }) =>
  eventApi.redpacketInfo(params.id);

export const ClaimDailyBetReward = (params: { ids: string[] }) =>
  eventApi.claimDailyBetReward(params.ids);
